import { create } from 'zustand';
import { TAvailabilityStore, TDate } from './domain';
import { DEFAULT_AVAILABILITY_STATE } from './state';
import { addMonths, format, setDate, startOfDay } from 'date-fns';
import { MONTH_FORMAT_DATE, SERVER_FORMAT_DATE } from '@/service/constants/dates';
import { isExpired10mins } from '@/service/utils/isExpired10mins';
import { getAvailabilityByMonth } from '@/service/api/Availability/getAvailabilityByMonth';
import { getAvailabilityByDay } from '@/service/api/Availability/getAvailabilityByDay';
import { getAvailabilityDayFull } from '@/service/api/FullCalendar/getAvailabilityDayFull';
import { validateSelections } from './validation';
import { usePartnerStore } from '../Partner/usePartnerStore';
import { EStatusLink } from '@/domain/Availability.domain';
import { newDateFromString } from '@/service/utils/getDateByTimezone';
import { createOptionsPrice } from '@/service/utils/createOptionsPrice';
import { getPricesForSchedules } from '@/service/api/Prices/getPricesForSchedules';
import { mapSchedulesWithPrices } from '@/service/utils/mapSchedulesWithPrices';
import { getOvernightsDurations, mapFullDayToDayItems } from '@/service/utils/mapFullDayToDayItem';
import { TSchedules, TTicketId } from '@/domain/FullCalendar.domain';

export const useAvailabilityStore = create<TAvailabilityStore>((set, get) => ({
    ...DEFAULT_AVAILABILITY_STATE,
    setDate: (date: TDate) => {
        const today = startOfDay(new Date());

        const newDate =
            date && newDateFromString(date) ? startOfDay(newDateFromString(date)) : today;

        if (newDate && newDate >= today) {
            set({ selectedDate: format(newDate, SERVER_FORMAT_DATE) });
        } else {
            set({ selectedDate: format(today, SERVER_FORMAT_DATE) });
        }
    },
    initDate: async (date, activityId, options) => {
        const { enable_gratuity_online, suggested_gratuity, gratuity_trips, siteurl } =
            usePartnerStore.getState();

        set({
            ...DEFAULT_AVAILABILITY_STATE,
            activityId,
            needTicketsNames: !!options?.needTicketsNames,
            partnerSlug: options?.partnerSlug || siteurl,
            enableTips:
                !!enable_gratuity_online &&
                !!suggested_gratuity.length &&
                (!gratuity_trips.length || gratuity_trips?.includes(activityId)),
            bookingFee: options?.bookingFee,
            bookingTax: options?.bookingTax,
            isTour: options?.isTour,
        });
        get().setDate(date);
    },
    getMonth: async (date: TDate) => {
        const activityId = get().activityId;
        const partnerSlug = get().partnerSlug;
        const newDate = date ? newDateFromString(date) : new Date();
        const month = format(newDate, MONTH_FORMAT_DATE);
        const loadedMonth = get().months[month];

        if (!loadedMonth || isExpired10mins(loadedMonth.timestamp)) {
            set((state) => ({
                months: { ...state.months, [month]: undefined },
                loadingMonth: true,
            }));

            const newMonthData = await getAvailabilityByMonth(partnerSlug, activityId, month);

            set((state) => ({
                months: {
                    ...state.months,
                    [month]: {
                        timestamp: new Date().getTime(),
                        data: newMonthData,
                    },
                },
                loadingMonth: false,
            }));

            if (newMonthData.length) {
                return {
                    date: newDate,
                    firstDay: newMonthData[0],
                };
            } else {
                return undefined;
            }
        } else {
            return {
                date: newDate,
                firstDay: loadedMonth.data[0],
            };
        }
    },
    initMonth: async (date, cb) => {
        const { getMonth } = get();
        const newDate = date ? newDateFromString(date) : new Date();

        const firstMonth = await getMonth(newDate);

        if (firstMonth) {
            cb(newDate);
            return;
        }

        const nextDate = addMonths(newDate, 1);
        const secondMonth = await getMonth(nextDate);

        if (secondMonth && secondMonth.date) {
            const secondMonthDay = setDate(secondMonth.date, +secondMonth.firstDay);

            cb(secondMonthDay);
            get().setDate(secondMonthDay);
            get().getDay(secondMonthDay);

            return;
        }

        const bulkMonths = Array.from({ length: 10 }, (_, i) => addMonths(nextDate, i + 1));

        const halfOfYear = await Promise.all(bulkMonths.map(getMonth));

        const firstAvailDay = halfOfYear.find((month) => !!month);

        if (firstAvailDay && firstAvailDay.date) {
            const nearestDate = setDate(firstAvailDay.date, +firstAvailDay.firstDay);

            cb(nearestDate);
            get().setDate(nearestDate);
            get().getDay(nearestDate);
        }
    },
    getDay: async (date: TDate, options) => {
        if (options?.noSchedules) {
            set({
                loadingDay: false,
                needTicketsNames: !!options?.needTicketsName,
                noSchedules: true,
                noCalendar: options?.noCalendar || false,
            });

            return;
        }

        const activityId = get().activityId;
        const partnerSlug = get().partnerSlug;
        const isTour = get().isTour;
        const dayKey = format(date ? newDateFromString(date) : new Date(), SERVER_FORMAT_DATE);
        const loadedDay = get().days[dayKey];
        const overnightsView = get().overnightsView;

        if (overnightsView) {
            get().getOvernightsDay(dayKey);
        }

        if (!loadedDay || isExpired10mins(loadedDay.timestamp)) {
            set((state) => ({
                days: { ...state.days, [dayKey]: undefined },
                loadingDay: true,
            }));

            const [newDayData, prices] = await Promise.all([
                getAvailabilityByDay(partnerSlug, activityId, dayKey),
                getPricesForSchedules(partnerSlug, activityId, dayKey, false, isTour),
            ]);

            set((state) => ({
                days: {
                    ...state.days,
                    [dayKey]: {
                        timestamp: new Date().getTime(),
                        data: mapSchedulesWithPrices(newDayData, prices),
                    },
                },
                loadingDay: false,
                needTicketsNames: !!newDayData?.[0]?.need_ticket_names,
            }));
        }
    },
    changeDay: (mode) => {
        const currentDate = newDateFromString(get().selectedDate);
        const currentMonth = currentDate.getDate().toString();

        const selectedMonth = get().getSelectedMonth(currentDate);
        const dayIndex = selectedMonth?.findIndex((date) => date === currentMonth);
        const newIndex =
            ((!!dayIndex || dayIndex === 0) &&
                selectedMonth?.[mode === 'prev' ? dayIndex - 1 : dayIndex + 1]) ||
            '';

        if (!!+newIndex && selectedMonth) {
            const newDate = setDate(currentDate, +newIndex);

            get().setDate(newDate);
            get().getDay(newDate);
        }
    },
    getOvernightsDay: async (date) => {
        const {
            activityId,
            partnerSlug,
            selectedDate,
            days,
            selectOption,
            needTicketsNames,
            isTour,
        } = get();

        const currentDate = date || selectedDate;
        const overnightsKey = currentDate + '-overnights';
        const overnightsDay = days[overnightsKey];

        if (!overnightsDay || isExpired10mins(overnightsDay.timestamp)) {
            set((state) => ({
                days: { ...state.days, [overnightsKey]: undefined },
                loadingOvernights: true,
            }));

            const [overnightsDayFull, overnightsPrices] = await Promise.all([
                getAvailabilityDayFull(
                    {
                        partnerId: partnerSlug,
                        overnights: 1,
                    },
                    {
                        trip_id: activityId,
                        date: currentDate,
                    }
                ),
                getPricesForSchedules(partnerSlug, activityId, currentDate, true, isTour),
            ]);

            const daySchedules = mapFullDayToDayItems(
                overnightsDayFull,
                needTicketsNames,
                overnightsPrices
            );
            const durations = getOvernightsDurations(overnightsDayFull);

            overnightsDayFull.additional_options.forEach((option) => {
                const isDefault =
                    !+option.is_disabled &&
                    Object.entries(option.values).find((value) => !!+value[1].is_default);

                if (isDefault) {
                    selectOption(option.id, isDefault[0]);
                }
            });

            set((state) => ({
                days: {
                    ...state.days,
                    [overnightsKey]: {
                        timestamp: new Date().getTime(),
                        data: [daySchedules],
                    },
                },
                loadingOvernights: false,
                overnightsDurations: {
                    ...state.overnightsDurations,
                    [overnightsKey]: durations,
                },
                overnights: {
                    ...state.overnights,
                    [currentDate]: overnightsDayFull,
                },
                selectedDayFull: overnightsDayFull,
            }));
        }
    },
    toggleOvernights: async () => {
        const currentValue = get().overnightsView;

        if (currentValue) {
            set({
                overnightsView: false,
                selectedOvernightsDuration: '',
            });
        } else {
            const getOvernightsDay = get().getOvernightsDay;

            await getOvernightsDay();

            await set({
                overnightsView: true,
            });
        }
    },
    setOvernightsDuration: async (duration) => {
        await set({
            selectedOvernightsDuration: duration,
        });
    },
    updateDay: async (isAddToCart) => {
        const { selectedDate, needTicketsNames, noSchedules, noCalendar, getDay, getMonth } = get();

        const currentDate = format(
            selectedDate ? newDateFromString(selectedDate) : new Date(),
            MONTH_FORMAT_DATE
        );

        const newDate = currentDate ? newDateFromString(currentDate) : new Date();
        const month = format(newDate, MONTH_FORMAT_DATE);
        const dayKey = format(
            selectedDate ? newDateFromString(selectedDate) : new Date(),
            SERVER_FORMAT_DATE
        );

        set((state) => ({
            months: { ...state.months, [month]: undefined },
            days: { ...state.days, [dayKey]: undefined },
        }));

        if (isAddToCart) {
            if (!noCalendar) {
                getMonth(currentDate);
            }
            getDay(selectedDate, { noSchedules, needTicketsName: needTicketsNames });
        }
    },
    getMonthAvail: async (partner_slug, trip_id, month) => {
        const monthAvail = await getAvailabilityByMonth(partner_slug, trip_id, month);
        set({ monthAvail });
    },
    getSelectedMonth: (date: TDate) => {
        if (!date) {
            return undefined;
        }

        const monthKey = format(newDateFromString(date), MONTH_FORMAT_DATE);

        return get().months[monthKey]?.data;
    },
    getSelectedDay: (date: TDate) => {
        if (!date) {
            return undefined;
        }

        const dayKey = format(newDateFromString(date), SERVER_FORMAT_DATE);

        const updatedKey = get().overnightsView ? dayKey + '-overnights' : dayKey;

        return get().days[updatedKey]?.data;
    },
    selectSchedule: async (scheduleId: number) => {
        const { overnightsView, selectedOvernightsDuration, overnights, selectedDate } = get();

        const overnightsDayFull = overnights[selectedDate];

        if (overnightsView && selectedOvernightsDuration && overnightsDayFull) {
            const selectedSchedule = overnightsDayFull.schedules.find(
                (schedule) => schedule.id === scheduleId
            );

            // FILTER not selected tickets
            const updatedSchedules = overnightsDayFull.schedules.map((schedule) => {
                if (schedule.id === scheduleId) {
                    const newTickets = Object.entries(schedule.tickets).reduce(
                        (res, [key, ticket]) => {
                            if (
                                ticket.duration === selectedOvernightsDuration ||
                                ticket.duration === null
                            ) {
                                res[key] = ticket;
                            }

                            return res;
                        },
                        {} as { [key: string]: TTicketId }
                    );

                    return {
                        ...schedule,
                        tickets: newTickets,
                    };
                }

                return schedule;
            });

            set({
                selectedSchedule: scheduleId,
                selectedDayFull: {
                    ...overnightsDayFull,
                    // @ts-expect-error new type after filter
                    schedules: updatedSchedules,
                },
            });

            overnightsDayFull.additional_options.forEach((option) => {
                const isDefault =
                    !+option.is_disabled &&
                    Object.entries(option.values).find((value) => !!+value[1].is_default);

                if (isDefault) {
                    get().selectOption(option.id, isDefault[0]);
                }
            });

            !!selectedSchedule?.tickets &&
                Object.values(selectedSchedule.tickets).some((ticket) => {
                    if (ticket.duration === selectedOvernightsDuration) {
                        const count = selectedSchedule.min_tickets
                            ? +selectedSchedule.min_tickets > ticket.avail
                                ? ticket.avail
                                : +selectedSchedule.min_tickets
                            : 1;

                        get().selectTicket(ticket.id, count);

                        return true;
                    }

                    return false;
                });

            return;
        }

        set({
            selectedSchedule: scheduleId,
            selectedDayFull: DEFAULT_AVAILABILITY_STATE.selectedDayFull,
        });

        const { partnerSlug, activityId, noCalendar, noSchedules, selectOption } = get();

        try {
            const newDayFull = await getAvailabilityDayFull(
                {
                    partnerId: partnerSlug,
                },
                {
                    trip_id: activityId,
                    schedule_id: scheduleId,
                    ...(!(noCalendar && noSchedules) && { date: selectedDate }),
                }
            );

            newDayFull.additional_options.forEach((option) => {
                const isDefault =
                    !+option.is_disabled &&
                    Object.entries(option.values).find((value) => !!+value[1].is_default);

                if (isDefault) {
                    selectOption(option.id, isDefault[0]);
                }
            });

            if (newDayFull) {
                set({
                    selectedDayFull: newDayFull,
                });
            } else {
                set({
                    selectedSchedule: undefined,
                });
            }

            set({
                status: DEFAULT_AVAILABILITY_STATE.status,
            });
        } catch (e) {
            set({
                selectedSchedule: undefined,
                status: DEFAULT_AVAILABILITY_STATE.status,
            });

            return;
        }
    },
    clearSelectedSchedule: () => {
        set({
            selectedSchedule: undefined,
            selectedDayFull: undefined,
            selectedTickets: DEFAULT_AVAILABILITY_STATE.selectedTickets,
            ticketNames: DEFAULT_AVAILABILITY_STATE.ticketNames,
            selectedOptions: DEFAULT_AVAILABILITY_STATE.selectedOptions,
            agreementSelected: DEFAULT_AVAILABILITY_STATE.agreementSelected,
            termsSelected: DEFAULT_AVAILABILITY_STATE.termsSelected,
            gratuity: DEFAULT_AVAILABILITY_STATE.gratuity,
            status: DEFAULT_AVAILABILITY_STATE.status,
            ticketsSum: DEFAULT_AVAILABILITY_STATE.ticketsSum,
            optionsSum: DEFAULT_AVAILABILITY_STATE.optionsSum,
            tipsPrice: DEFAULT_AVAILABILITY_STATE.tipsPrice,
        });
    },
    selectTicket: (ticketId, count) => {
        set({
            selectedTickets: {
                ...get().selectedTickets,
                [ticketId]: count,
            },
        });

        const { selectedDayFull, selectedSchedule, selectedTickets, selectedOptions } = get();

        const currentSchedule = ((selectedDayFull?.schedules &&
            (Array.isArray(selectedDayFull?.schedules)
                ? selectedDayFull?.schedules
                : Object.values(selectedDayFull.schedules))) ||
            []) as TSchedules;

        const schedule = currentSchedule.find(({ id }) => id === selectedSchedule);
        const ticketsSum = Object.entries(selectedTickets)
            .reduce((sum, [id, count]) => {
                const price =
                    (schedule?.tickets &&
                        Object.values(schedule.tickets)?.find((ticket) => +ticket.id === +id)
                            ?.price) ||
                    selectedDayFull?.tickets?.find((ticket) => +ticket.id === +id)?.price ||
                    0;

                return sum + +price * count;
            }, 0)
            .toFixed(2);

        const optionsSum = createOptionsPrice(
            selectedOptions,
            ticketsSum,
            selectedDayFull?.additional_options
        );

        set((state) => ({
            ...state,
            status: validateSelections(state),
            ticketsSum,
            optionsSum,
        }));

        get().countTipsPrice(get().gratuity ?? 0, !!get().isCustom);
    },
    addTicketNames: (ticket_id: string, guest_id: number) => {
        set({
            ticketNames: {
                ...get().ticketNames,
                [ticket_id]: {
                    ...get().ticketNames[ticket_id],
                    [guest_id]: { first_name: '', last_name: '' },
                },
            },
        });

        set((state) => ({
            ...state,
            status: validateSelections(state),
        }));
    },
    removeTicketNames: (ticket_id: string, guest_id: number) => {
        delete get().ticketNames[ticket_id][guest_id + 1];
        set({ ticketNames: get().ticketNames });

        set((state) => ({
            ...state,
            status: validateSelections(state),
        }));
    },
    changeTicketNames: (ticket_id: string, guest_id: number, name: string, value: string) => {
        set({
            ticketNames: {
                ...get().ticketNames,
                [ticket_id]: {
                    ...get().ticketNames[ticket_id],
                    [guest_id]: {
                        ...get().ticketNames[ticket_id][guest_id],
                        [name]: value,
                    },
                },
            },
        });

        set((state) => ({
            ...state,
            status: validateSelections(state),
        }));
    },
    selectOption: (optionId, value) => {
        set({
            selectedOptions: {
                ...get().selectedOptions,
                [+optionId]: value,
            },
        });

        const { selectedDayFull, selectedOptions, ticketsSum } = get();

        const optionsSum = createOptionsPrice(
            selectedOptions,
            ticketsSum,
            selectedDayFull?.additional_options
        );

        set((state) => ({
            ...state,
            status: validateSelections(state),
            optionsSum,
        }));

        get().countTipsPrice(get().gratuity ?? 0, !!get().isCustom);
    },
    selectGratuity: (value, isCustom) => {
        if (isCustom) {
            if (!value) {
                set({
                    gratuity: undefined,
                    isCustom: 0,
                });
            } else if (+value) {
                set({
                    gratuity: +value,
                    isCustom: 1,
                });
            }
        } else {
            set({
                gratuity: +value,
                isCustom: 0,
            });
        }

        set((state) => ({
            ...state,
            status: validateSelections(state),
        }));

        get().countTipsPrice(+value, !!isCustom);
    },
    selectComment: (value) => {
        set({
            comment: value,
        });
        set((state) => ({
            ...state,
            status: validateSelections(state),
        }));
    },
    selectAgreement: (value) => {
        set({
            agreementSelected: value,
        });
        set((state) => ({
            ...state,
            status: validateSelections(state),
        }));
    },
    selectTerms: (index) => {
        set(({ termsSelected, selectedDayFull }) => {
            const newTerms = termsSelected
                ? [...termsSelected]
                : Array(selectedDayFull?.terms?.length || 0).fill(false);
            const oldValue = newTerms[index];

            newTerms[index] = !oldValue;

            return {
                termsSelected: newTerms,
            };
        });
        set((state) => ({
            ...state,
            status: validateSelections(state),
        }));
    },
    setErrorMessage: (msg) => {
        if (msg) {
            set({
                status: {
                    isValid: true,
                    error: true,
                    message: msg,
                    touched: true,
                    link: EStatusLink.ADD_TO_CART,
                },
            });
        }
    },
    countTipsPrice: (percentage, isCustom) => {
        if (isCustom) {
            set({ tipsPrice: DEFAULT_AVAILABILITY_STATE.tipsPrice });
        } else {
            const { bookingFee, bookingTax } = get();
            const tax = bookingTax ? bookingTax / 100 : 0;
            const fee = bookingFee ? bookingFee / 100 : 0;

            const ticketSum = +get().ticketsSum;
            const optionSum = +get().optionsSum;
            const taxSum = ticketSum * tax;
            const feeSum = (ticketSum + taxSum + optionSum) * fee;
            const sum = ticketSum + optionSum + taxSum + feeSum;

            const tipsPrice = (sum * percentage) / 100;

            set({
                tipsPrice,
                totalSum: sum + tipsPrice,
            });
        }
    },
}));
